import * as THREE from 'three';
import type { IVoxelKafes, IDolumSonuc } from './simulasyon/IDokumSim';
import type { IKatilasmaSonuc } from './simulasyon/IKatilasmaSim';

/** Dolum simülasyonu çıktısı (kafes + sonuç birlikte) */
export interface IDolumSimSonuc {
  kafes: IVoxelKafes;
  sonuc: IDolumSonuc;
}

/**
 * Döküm Kontrol — paylaşılan sözleşme (interface contract).
 *
 * Tüm modüller (gösterge motoru, yolluk yöneticisi, HUD bileşeni) bu tiplere göre çalışır.
 * Çekirdek fikir: tüm göstergeler TEK bir cidar-kalınlığı + voxel doluluk alanından türetilir.
 * Bu yüzden hızlı ve tutarlıdır.
 */

// ─────────────────────────────────────────────────────────────────────────
// Temel tipler
// ─────────────────────────────────────────────────────────────────────────

export type GostergeTipi = 'ince_cidar' | 'sicak_nokta' | 'ani_kesit' | 'hapsolan_hava';
export type YollukTipi =
  | 'dolum_girisi' | 'besleyici' | 'hava_deligi' | 'filtre'
  | 'sogutucu' | 'maca' | 'tasma' | 'sensor';
export type EtiketModu = 'hepsi' | 'hover' | 'secim' | 'gizli';
export type DokumNotu = 'A' | 'B' | 'C' | 'D' | 'F';
/** Dolum girişi akış profili (zaman içinde debi modülasyonu) — eski SPH FikoDokumGirisi'nden */
export type AkisProfili = 'sabit' | 'rampa' | 'sinus' | 'puls';
/**
 * Besleyici (riser) tipi — gerçek dünya besleyici davranışları:
 *  • acik      — atmosfere açık (üstü hava; hafif yavaş soğur, pipe görünür)
 *  • kor       — kör/blind (her tarafı kum; standart soğuma)
 *  • ekzotermik— ekzotermik gömlek (yanar, ısı verir → çok geç katılaşır, uzun besler)
 *  • yalitimli — yalıtım gömleği (ısı kaybı azalır → geç katılaşır)
 */
export type BesleyiciTipi = 'acik' | 'kor' | 'ekzotermik' | 'yalitimli';
/** Soğutucu (chill) malzemesi — ısı çekiş şiddetini belirler */
export type SogutucuMalzeme = 'celik' | 'bakir' | 'grafit';
/**
 * Dolum çözüm kalitesi:
 *  • onizleme — CA hücresel-otomat (JS, hızlı, anında)
 *  • final    — SPH WebGPU parçacık dolumu (gerçek serbest yüzey; köprüyle voxel'e yazılır)
 */
export type CozumKalitesi = 'onizleme' | 'final';

/** Besleyici tipi → kalıp arayüz ısı-kaybı çarpanı (1 = kum). Düşük = ısıyı tutar. */
export const BESLEYICI_TIP_H_CARPAN: Record<BesleyiciTipi, number> = {
  acik: 1.0,
  kor: 0.9,
  yalitimli: 0.35,
  ekzotermik: 0.15,
};
export const BESLEYICI_TIP_BASLIK: Record<BesleyiciTipi, string> = {
  acik: 'Açık', kor: 'Kör', ekzotermik: 'Ekzotermik', yalitimli: 'Yalıtımlı',
};
/** Soğutucu malzemesi → ısı çekiş çarpanı (kuma göre kaç kat) */
export const SOGUTUCU_H_CARPAN: Record<SogutucuMalzeme, number> = {
  celik: 6, bakir: 12, grafit: 8,
};
export const SOGUTUCU_BASLIK: Record<SogutucuMalzeme, string> = {
  celik: 'Çelik', bakir: 'Bakır', grafit: 'Grafit',
};

/** Sanal sensör ölçümü — sim sonrası bir noktadaki değerler */
export interface ISensorVeri {
  dolumSn?: number;        // o voxel'e metalin varış süresi (s)
  katilasmaSn?: number;    // o voxel'in katılaşma süresi (s)
  kalinlikMm?: number;     // yerel kesit kalınlığı (mm)
}

export const GOSTERGE_RENK: Record<GostergeTipi, string> = {
  ince_cidar: '#34d3ff',
  sicak_nokta: '#ff3b3b',
  ani_kesit: '#ffb02e',
  hapsolan_hava: '#a855f7',
};

export const GOSTERGE_BASLIK: Record<GostergeTipi, string> = {
  ince_cidar: 'İnce cidar',
  sicak_nokta: 'Sıcak nokta',
  ani_kesit: 'Ani kesit',
  hapsolan_hava: 'Hapsolan hava',
};

export const YOLLUK_RENK: Record<YollukTipi, string> = {
  dolum_girisi: '#22c55e',
  besleyici: '#ef4444',
  hava_deligi: '#a855f7',
  filtre: '#eab308',
  sogutucu: '#38bdf8',
  maca: '#94a3b8',
  tasma: '#f97316',
  sensor: '#e11d8f',
};

export const YOLLUK_BASLIK: Record<YollukTipi, string> = {
  dolum_girisi: 'Dolum Girişi',
  besleyici: 'Besleyici',
  hava_deligi: 'Hava Deliği',
  filtre: 'Filtre',
  sogutucu: 'Soğutucu',
  maca: 'Maça',
  tasma: 'Taşma',
  sensor: 'Sensör',
};

// ─────────────────────────────────────────────────────────────────────────
// Çözüm geometrisi — orkestratör doldurur, motorlar tüketir
// ─────────────────────────────────────────────────────────────────────────

export interface IVoxelGrid {
  /** Hücre sayısı [nx, ny, nz] */
  boyut: [number, number, number];
  /** Hücre kenar uzunluğu (mm, dünya uzayı) */
  hucreMm: number;
  /** Grid başlangıç köşesi (dünya) */
  origin: THREE.Vector3;
  /** Doluluk: 1 = parça içi, 0 = boş (uzunluk nx*ny*nz) */
  doluluk: Uint8Array;
}

/** Birleştirilmiş, dünya uzayına alınmış geometri + yardımcı alanlar */
export interface ICozumGeometri {
  positions: Float32Array;   // 3/vertex, dünya uzayı
  normals: Float32Array;     // 3/vertex, dünya uzayı
  indices: Uint32Array;
  vertexSayisi: number;
  bbox: THREE.Box3;
  /** Kaba voxel doluluk (opsiyonel, artık kullanılmıyor — yerini tavanMesafe aldı). */
  voxel?: IVoxelGrid;
  /**
   * Per-vertex "tavan mesafesi" (mm): vertex'ten yukarı yönde ilk yüzeye uzaklık.
   * Hapsolan hava göstergesi bunu kullanır. Yoksa gösterge atlanır.
   */
  tavanMesafe?: Float32Array;
  /** Yukarı (yerçekiminin tersi) yön — döküm/akış oryantasyonu. Yoksa (0,1,0). */
  yukariYon?: THREE.Vector3;
}

// ─────────────────────────────────────────────────────────────────────────
// Göstergeler
// ─────────────────────────────────────────────────────────────────────────

export interface IGostergeAyar {
  /** Minimum güvenli cidar (mm) — alaşıma göre. Altındaki ince cidar sayılır. */
  minCidarMm: number;
  /** Sıcak nokta için modül (kalınlık/2, cm) eşiği. Üstü sıcak nokta. */
  sicakNoktaModulCm: number;
  /** Ani kesit: komşu kalınlık oranı eşiği (örn. 2.5 = 2.5 kat fark). */
  kesitOraniEsik: number;
  /** Bir bölge sayılması için minimum vertex sayısı (gürültü filtresi). */
  minBolgeVertex: number;
}

export const VARSAYILAN_GOSTERGE_AYAR: IGostergeAyar = {
  minCidarMm: 2.5,
  sicakNoktaModulCm: 0.8,
  kesitOraniEsik: 2.5,
  minBolgeVertex: 8,
};

/** Tek bir tespit: kümelenmiş sorun bölgesi */
export interface IDokumBolge {
  id: string;
  tip: GostergeTipi;
  merkez: THREE.Vector3;        // dünya koordinatı (etiket/kamera için)
  vertexIndisleri: number[];    // highlight için (positions içindeki indis)
  siddet: number;               // 0-1 normalize
  deger: number;                // kalınlık mm / modül cm / gradyan oranı
  birim: string;                // 'mm' | 'cm' | 'x'
  baslik: string;
  aciklama: string;
  oneri: string;
  /** Yerleştirilen yollukla çözüldü mü (besleyici besliyor / hava deliği var / giriş yakın). */
  cozuldu?: boolean;
  /** Çözüm açıklaması — yolluk kapsama değerlendirmesinden. */
  cozumMesaji?: string;
}

export interface IGostergeSonuc {
  bolgeler: IDokumBolge[];
  /** Per-vertex kalınlık (mm) — ısı haritası renklendirmesi için */
  kalinlikAlani: Float32Array;
  /** İstatistik özeti */
  minKalinlik: number;
  maxKalinlik: number;
  ortKalinlik: number;
}

export interface IDokumNotSonuc {
  puan: number;          // 0-100 (100 = sorunsuz)
  not: DokumNotu;        // A-F
  kritikSayi: number;
  toplamUyari: number;
  ozet: string;          // "Riskli: 2 sıcak nokta beslemesiz" gibi
}

/** Gösterge motoru — saf fonksiyonlar, test edilebilir (Agent A) */
export interface IGostergeMotoru {
  hesapla(geo: ICozumGeometri, kalinlikAlani: Float32Array, ayar: IGostergeAyar): IGostergeSonuc;
  notHesapla(sonuc: IGostergeSonuc): IDokumNotSonuc;
}

// ─────────────────────────────────────────────────────────────────────────
// Yolluk sistemi
// ─────────────────────────────────────────────────────────────────────────

export interface IYollukEleman {
  id: string;
  tip: YollukTipi;
  pozisyon: THREE.Vector3;     // dünya koordinatı
  /** Akış/eksen yönü (normalize). Dolum girişi için metalin akış yönü; diğerleri için varsayılan +Y. */
  yon: THREE.Vector3;
  capMm: number;
  yukseklikMm: number;
  /** Dolum girişi debisi (gram/saniye) — kaç gram metal akacağı. Sadece dolum_girisi için anlamlı. */
  debiGramSn?: number;
  /** Dolum girişi akış profili (zaman içinde debi modülasyonu). Sadece dolum_girisi. Varsayılan 'sabit'. */
  akisProfili?: AkisProfili;
  /** Zamanlı senaryo: eleman t=acilmaSn'de devreye girer (s). Yoksa 0 (baştan açık). */
  acilmaSn?: number;
  /** Zamanlı senaryo: eleman t=kapanmaSn'de kapanır (s). Yoksa hiç kapanmaz. */
  kapanmaSn?: number;
  /** Besleyici tipi (kör/açık/ekzotermik/yalıtımlı). Sadece besleyici. Varsayılan 'acik'. */
  besleyiciTipi?: BesleyiciTipi;
  /** Soğutucu malzemesi (çelik/bakır/grafit) → ısı çekiş şiddeti. Sadece sogutucu. */
  sogutucuMalzeme?: SogutucuMalzeme;
  /** Sanal sensör ölçüm sonucu (sim sonrası doldurulur). Sadece sensor tipi. */
  sensorVeri?: ISensorVeri;
  otomatikMi: boolean;         // öneri mi (true) yoksa manuel mi (false)
  kabulEdildi: boolean;        // hayalet öneri kabul edildiyse true
  gecerli: boolean;            // doğrulama ✓/✗
  dogrulamaMesaji: string;
  /** Hangi tespiti hedefliyor (besleyici→sıcak nokta, delik→hava) */
  hedefBolgeId?: string;
}

export interface IYollukDogrulama {
  gecerli: boolean;
  mesaj: string;
}

/** Yolluk elemanı kısmi güncelleme (3D düzenleyici / özellik paneli) */
export interface IYollukGuncelleme {
  pozisyon?: THREE.Vector3;
  yon?: THREE.Vector3;
  capMm?: number;
  yukseklikMm?: number;
  debiGramSn?: number;
  akisProfili?: AkisProfili;
  acilmaSn?: number;
  kapanmaSn?: number;
  besleyiciTipi?: BesleyiciTipi;
  sogutucuMalzeme?: SogutucuMalzeme;
}

/** Yolluk yöneticisi — öner/doğrula saf mantık (Agent B) */
export interface IYollukMotoru {
  /** Sıcak nokta + hapsolan havaya göre otomatik öneri üret (hayalet, kabulEdildi=false) */
  oner(sonuc: IGostergeSonuc, geo: ICozumGeometri, alasim: string): IYollukEleman[];
  /** Tek bir elemanı doğrula (besleyici sıcak noktayı besliyor mu, delik havayı çıkarıyor mu) */
  dogrula(eleman: IYollukEleman, sonuc: IGostergeSonuc, geo: ICozumGeometri): IYollukDogrulama;
}

// ─────────────────────────────────────────────────────────────────────────
// Orkestratör cephesi — HUD bileşeni buna göre çalışır (Agent C tüketir)
// ─────────────────────────────────────────────────────────────────────────

export interface IDokumKontrolGirdi {
  meshler: THREE.Mesh[];
  alasim?: string;           // varsayılan 'AlSi (döküm alüminyum)'
  ayar?: Partial<IGostergeAyar>;
  onProgress?: (yuzde: number) => void;
}

export interface IKontrolSonuc {
  gosterge: IGostergeSonuc;
  not: IDokumNotSonuc;
  geo: ICozumGeometri;
}

/** Besleyici modül yeterlilik kontrolü (Chvorinov) */
export interface IBesleyiciModulKontrol {
  id: string;
  modulBesleyiciCm: number;
  modulHedefCm: number;
  yeterli: boolean;       // besleyici modülü ≥ 1.2 × hedef kesit modülü
  mesaj: string;
}

/** Kum döküm methoding (yöntemleme) hesabı sonucu */
export interface IMethodingSonuc {
  alasimId: string;
  alasimAd: string;
  kumId: string;
  kumAd: string;
  dokumSicaklikMinC: number;
  dokumSicaklikMaxC: number;
  yogunlukGcm3: number;
  cekmeYuzde: number;
  parcaHacimCm3: number;
  parcaKutleKg: number;
  toplamDebiGramSn: number;
  dolumSuresiSn: number;          // kütle(g) / toplam debi(g/s)
  girisHiziMs: number;
  girisHiziLimitMs: number;       // alaşıma göre (Al ~0.5)
  girisHiziUygun: boolean;
  /** Weber sayısı We=ρv²L/σ — yüzey türbülansı/oksit sürüklenme göstergesi (>100 riskli) */
  weberSayisi: number;
  oksitRiski: boolean;
  besleyiciHacimCm3: number;
  yollukHacimCm3: number;
  toplamDokulenKg: number;
  verimYuzde: number;             // parça / toplam dökülen
  besleyiciModul: IBesleyiciModulKontrol[];
  uyarilar: string[];
}

export interface IDokumKontrolMotor {
  kontrolEt(girdi: IDokumKontrolGirdi): Promise<IKontrolSonuc>;
  /**
   * Seçilen alaşım+kum ile yöntemleme hesabı (ağırlık, dolum süresi, hız, verim).
   * manuelHacimCm3 verilirse otomatik (yaklaşık) hacim yerine o kullanılır (CATIA değeri vb.).
   * kontrolEt sonrası çağrılır.
   */
  methodingHesapla(alasimId: string, kumId: string, manuelHacimCm3?: number): IMethodingSonuc | null;
  /**
   * Yerleştirilen yollukların bölgeleri ne kadar çözdüğünü yeniden değerlendir.
   * Voxel/kalınlık YENİDEN HESAPLANMAZ (ucuz) — sadece bölge↔yolluk kapsaması.
   * kontrolEt sonrası + her yolluk ekleme/silme/kabul/güncellemede çağrılır.
   */
  yollukKapsamaGuncelle(): { gosterge: IGostergeSonuc; not: IDokumNotSonuc } | null;
  /** Döküm/akış yukarı yönünü ayarla (yerçekiminin tersi). Sonraki kontrolEt'te kullanılır. */
  yukariYonAyarla(yukari: THREE.Vector3): void;
  /** Yukarı yönü tersine çevir (parçayı baş aşağı dök). Yeni yukarı vektörünü döndürür. */
  yukariYonTersine(): THREE.Vector3;
  /** Dolum/katılaşma voxel çözünürlüğünü ayarla (en uzun eksendeki hücre sayısı; 0 = otomatik). */
  dolumCozunurlukAyarla(n: number): void;
  /** Dolum çözüm kalitesi: 'onizleme' (CA) | 'final' (SPH WebGPU). Desteksizse CA'ya düşer. */
  cozumKalitesiAyarla?(k: CozumKalitesi): void;
  /** Döküm prosesi: döküm sıcaklığı (0 = alaşım vars.) + kalıp ön ısıtma °C. */
  prosesAyarla?(ayar: { dokumSicaklikC?: number; kalipSicaklikC?: number }): void;
  /** Parametrik malzeme: sayısal alaşım alanlarını DB'nin üstüne yaz (T_liquidus, gizliIsi, cekmeOrani...). */
  alasimAyarla?(override: Record<string, number>): void;
  /** Faz 1 — Dolum simülasyonu: kaviteyi voxelle + fill-front. kontrolEt + yolluk girişi sonrası. async (ilerleme). */
  dolumSimulasyonu(alasimId: string, ilerle?: (yuzde: number) => void): Promise<IDolumSimSonuc | null>;
  /** Faz 2 — Katılaşma simülasyonu (entalpi FDM): Niyama çekme, sıcak nokta, sıcak yırtılma. async (ilerleme). */
  katilasmaSimulasyonu(alasimId: string, kumId: string, baslangicT?: Float32Array, ilerle?: (yuzde: number) => void): Promise<IKatilasmaSonuc | null>;
  /** DOE: verilen döküm sıcaklığıyla tahribatsız varyant dolum (proses penceresi). */
  varyantDolum(alasimId: string, dokumSicaklikC: number): Promise<IDolumSonuc | null>;
  /** Yolluk öneri parametreleri (max besleyici sayısı, max çap mm). */
  yollukAyarla?(ayar: { maxBesleyici?: number; besleyiciMaxCapMm?: number }): void;
  yollukOner(): IYollukEleman[];
  yollukEkle(tip: YollukTipi, pozisyon: THREE.Vector3, yon?: THREE.Vector3): IYollukEleman;
  /** Var olan elemanın yönünü güncelle (sürükle/ince ayar) + yeniden doğrula */
  yollukYonGuncelle(id: string, yon: THREE.Vector3): IYollukEleman | null;
  /** Elemanın herhangi bir alanını güncelle (konum/yön/çap/yükseklik) + yeniden doğrula */
  yollukGuncelle(id: string, kismi: IYollukGuncelleme): IYollukEleman | null;
  yollukKabul(id: string): IYollukEleman | null;   // hayaleti gerçeğe çevir + yeniden doğrula
  yollukSil(id: string): void;
  yolluklar(): IYollukEleman[];
  /** Sensör yolluklarının ölçümlerini son dolum+katılaşma sonuçlarından doldur. */
  sensorleriOku(): void;
  dispose(): void;
}
